import { account, databases, DATABASE_ID, COLLECTIONS, Query } from '../lib/appwrite';

/**
 * User Service - Handles all user-related database operations with Appwrite
 *
 * IMPORTANT: User creation happens through the signup flow (SignUp.jsx)
 * which uses account.create(). The user_profiles entry is created in AuthContext.
 *
 * This service handles: fetching, updating, deleting, and approving users
 */

// Fetch all users (admin only - based on permissions)
export const fetchAllUsers = async () => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.USER_PROFILES,
      [
        Query.orderDesc('$createdAt'),
        Query.limit(100)
      ]
    );

    return { data: response.documents, error: null };
  } catch (error) {
    console.error('Error fetching users:', error);
    return { data: null, error };
  }
};

// Fetch single user by ID
export const fetchUserById = async (userId) => {
  try {
    const data = await databases.getDocument(
      DATABASE_ID,
      COLLECTIONS.USER_PROFILES,
      userId
    );

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching user:', error);
    return { data: null, error };
  }
};

// Update user profile
export const updateUser = async (userId, updates) => {
  try {
    // Convert camelCase to snake_case if needed
    const dbUpdates = {};
    Object.keys(updates).forEach(key => {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      dbUpdates[snakeKey] = updates[key];
    });

    const data = await databases.updateDocument(
      DATABASE_ID,
      COLLECTIONS.USER_PROFILES,
      userId,
      dbUpdates
    );

    return { data, error: null };
  } catch (error) {
    console.error('Error updating user:', error);
    return { data: null, error };
  }
};

// Delete user
// NOTE: This should ideally call an Appwrite Function that handles cascade deletion
// For now, it only deletes the user profile. You may need to create an Appwrite Function
// similar to the Supabase RPC function 'delete_user_with_auth'
export const deleteUser = async (userId) => {
  try {
    // TODO: Replace with Appwrite Function call for proper cascade deletion
    // For now, delete user profile (Appwrite Functions handle cascade for related data)
    await databases.deleteDocument(
      DATABASE_ID,
      COLLECTIONS.USER_PROFILES,
      userId
    );

    // Note: To fully delete the auth user, you'd need to use the Users API with an API key
    // This is best done in an Appwrite Function for security

    return { success: true };
  } catch (error) {
    console.error('Error in deleteUser:', error);
    throw error;
  }
};

// Delete multiple users
export const deleteMultipleUsers = async (userIds) => {
  try {
    // Delete users one by one
    const deletePromises = userIds.map(userId => deleteUser(userId));
    const results = await Promise.all(deletePromises);

    // Check if any deletions failed
    const failedDeletions = results.filter(result => result.error);

    if (failedDeletions.length > 0) {
      throw new Error(`Failed to delete ${failedDeletions.length} user(s)`);
    }

    return { error: null };
  } catch (error) {
    console.error('Error deleting multiple users:', error);
    return { error };
  }
};

// Helper function to determine user status based on role and email verification
export const getUserStatus = (user) => {
  if (!user) return 'Inactive';
  if (!user.approved && !user.emailVerified) return 'Pending Approval';

  // All approved users should be Active
  if (user.approved) return 'Active';

  return 'Inactive';
};

// Helper function to get role-based permissions
export const getRolePermissions = (role) => {
  const roleMap = {
    'admin': {
      viewDashboard: true,
      manageSamples: true,
      manageDewars: true,
      viewReports: true,
      manageUsers: true,
      editSettings: true
    },
    'lab_manager': {
      viewDashboard: true,
      manageSamples: true,
      manageDewars: true,
      viewReports: true,
      manageUsers: false,
      editSettings: false
    },
    'technician': {
      viewDashboard: true,
      manageSamples: true,
      manageDewars: false,
      viewReports: true,
      manageUsers: false,
      editSettings: false
    },
    'user': {
      viewDashboard: true,
      manageSamples: false,
      manageDewars: false,
      viewReports: true,
      manageUsers: false,
      editSettings: false
    }
  };

  return roleMap[role?.toLowerCase()] || roleMap['user'];
};

// Fetch pending users (unapproved)
export const fetchPendingUsers = async () => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.USER_PROFILES,
      [
        Query.equal('approved', false),
        Query.orderDesc('$createdAt'),
        Query.limit(100)
      ]
    );

    return { data: response.documents, error: null };
  } catch (error) {
    console.error('Error fetching pending users:', error);
    return { data: null, error };
  }
};

// Approve a user
export const approveUser = async (userId) => {
  try {
    const user = await account.get();
    if (!user) throw new Error('Not authenticated');

    const data = await databases.updateDocument(
      DATABASE_ID,
      COLLECTIONS.USER_PROFILES,
      userId,
      {
        approved: true,
        approved_by: user.$id,
        approved_at: new Date().toISOString()
      }
    );

    return { data, error: null };
  } catch (error) {
    console.error('Error approving user:', error);
    return { data: null, error };
  }
};

// Reject/Unapprove a user
export const rejectUser = async (userId) => {
  try {
    const data = await databases.updateDocument(
      DATABASE_ID,
      COLLECTIONS.USER_PROFILES,
      userId,
      {
        approved: false,
        approved_by: null,
        approved_at: null
      }
    );

    return { data, error: null };
  } catch (error) {
    console.error('Error rejecting user:', error);
    return { data: null, error };
  }
};

// Check if current user can approve others
export const canApproveUsers = async () => {
  try {
    const user = await account.get();
    if (!user) return false;

    const data = await databases.getDocument(
      DATABASE_ID,
      COLLECTIONS.USER_PROFILES,
      user.$id
    );

    return data.role === 'admin' && data.approved === true;
  } catch (error) {
    console.error('Error checking approval permissions:', error);
    return false;
  }
};

// Convert database user to UI format
export const mapUserToUIFormat = (dbUser) => {
  if (!dbUser) return null;

  return {
    id: dbUser.$id,
    name: dbUser.full_name,
    email: dbUser.email,
    role: formatRoleForDisplay(dbUser.role),
    status: getUserStatus(dbUser),
    permissions: getRolePermissions(dbUser.role),
    email_verified: dbUser.email_verified,
    approved: dbUser.approved,
    approved_by: dbUser.approved_by,
    approved_at: dbUser.approved_at,
    created_at: dbUser.$createdAt,
    updated_at: dbUser.$updatedAt
  };
};

// Convert UI format to database format
export const mapUIFormatToUser = (uiUser) => {
  if (!uiUser) return null;

  return {
    full_name: uiUser.name,
    email: uiUser.email,
    role: formatRoleForDatabase(uiUser.role)
  };
};

// Format role for display (database format to UI format)
const formatRoleForDisplay = (role) => {
  const roleMap = {
    'admin': 'Administrator',
    'lab_manager': 'Lab Manager',
    'technician': 'Technician',
    'user': 'Student'
  };

  return roleMap[role?.toLowerCase()] || 'Student';
};

// Format role for database (UI format to database format)
const formatRoleForDatabase = (role) => {
  const roleMap = {
    'Administrator': 'admin',
    'Lab Manager': 'lab_manager',
    'Technician': 'technician',
    'Student': 'user'
  };

  return roleMap[role] || 'user';
};
